import { useState, type ReactNode } from 'react';
import { Buildings, Plus } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { workspacesApi } from '@/api/endpoints';
import { workspaceStore } from '@/auth/stores';
import { useAuth, useWorkspace } from '@/hooks/useAuth';

export function RequireWorkspace({ children }: { children: ReactNode }) {
  const { workspaces, activeId } = useWorkspace();
  const { refresh } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  if (workspaces.length && activeId) return <>{children}</>;

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const workspace = await workspacesApi.create({ name: name.trim() });
      workspaceStore.set(workspace.id);
      queryClient.clear();
      await refresh();
      toast.success(`${workspace.name} is ready`);
    } catch (error: any) {
      toast.error(error?.message ?? 'Could not create the workspace');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="app-page">
      <Card className="max-w-[560px] mx-auto mt-12">
        <CardContent className="p-6 space-y-4">
          <div className="size-11 rounded-xl grid place-items-center bg-primary/10 text-primary text-xl">
            <Buildings size={22} />
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground m-0">
              Create a workspace
            </h2>
            <p className="text-sm text-muted-foreground mt-1.5 mb-0 leading-relaxed">
              A workspace holds your blogs, media and connected social accounts,
              and is what you invite teammates into. You are not in one yet.
            </p>
          </div>

          <div className="flex gap-2">
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') create();
              }}
              placeholder="Acme Marketing"
              autoFocus
            />
            <Button
              disabled={!name.trim()}
              loading={saving}
              onClick={create}
              className="shrink-0"
            >
              <Plus size={16} className="mr-1" />
              Create
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
